import React, { useState } from 'react';
import { runAdminAction } from '../../utils/adminActions';
import { MessageSetter } from '../../utils/messages';
import { Icons } from '../../utils/icons';

interface AdminToolsProps {
  setMessage: MessageSetter;
}

const AdminToolsSection: React.FC<AdminToolsProps> = ({ setMessage }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isWiping, setIsWiping] = useState(false);
  const [isClearingSessions, setIsClearingSessions] = useState(false);
  const [isClearingFeatures, setIsClearingFeatures] = useState(false);

  const handleClearSessions = async () => {
    setIsClearingSessions(true);
    setMessage(null);

    await runAdminAction('/api/admin/clear-sessions', {
      confirmPrompt: 'Are you sure you want to clear all sessions and audit logs? This action cannot be undone!',
      setMessage,
      successMessage: 'All sessions and audit logs cleared successfully.',
      onSuccess: () => {
        setTimeout(() => window.location.reload(), 2000);
      },
    });

    setIsClearingSessions(false);
  };

  const handleClearFeatures = async () => {
    setIsClearingFeatures(true);
    setMessage(null);

    await runAdminAction('/api/admin/clear-features', {
      confirmPrompt: 'Are you sure you want to clear all features and sub-dependencies? This action cannot be undone!',
      setMessage,
      successMessage: 'All features and sub-dependencies cleared successfully.',
      onSuccess: () => {
        setTimeout(() => window.location.reload(), 2000);
      },
    });

    setIsClearingFeatures(false);
  };

  const handleWipeDatabase = async () => {
    if (confirmText !== 'CONFIRM') {
      setMessage({ type: 'error', text: 'Please type CONFIRM exactly to proceed' });
      return;
    }

    setIsWiping(true);
    setMessage(null);

    await runAdminAction('/api/admin/wipe-database', {
      confirmPrompt: 'Are you absolutely sure you want to wipe the entire database? This action cannot be undone!',
      setMessage,
      successMessage: 'Database wiped successfully. All data has been deleted.',
      onSuccess: () => {
        setConfirmText('');
        setTimeout(() => window.location.reload(), 2000);
      },
    });

    setIsWiping(false);
  };

  return (
    <div className="flex flex-col gap-lg">
      {/* Clear Sessions */}
      <div
        className="card"
        style={{
          border: '2px solid var(--warning)',
          backgroundColor: 'var(--warning-transparent)',
        }}
      >
        <h2 style={{ color: 'var(--warning)', marginBottom: '1rem' }}>{Icons.delete} Clear Sessions</h2>
        <p className="subtitle mb-md">This will permanently delete:</p>
        <ul className="subtitle mb-md" style={{ paddingLeft: '1.5rem' }}>
          <li>All testing sessions</li>
          <li>All audit logs</li>
        </ul>
        <p
          style={{
            color: 'var(--warning)',
            fontWeight: 'bold',
            marginBottom: '1rem',
          }}
        >
          Products, features, and sub-dependencies will remain.
        </p>

        <button
          className="btn"
          onClick={handleClearSessions}
          disabled={isClearingSessions}
          style={{
            backgroundColor: !isClearingSessions ? '#f39c12' : '#95a5a6',
            color: 'white',
            cursor: !isClearingSessions ? 'pointer' : 'not-allowed',
            opacity: !isClearingSessions ? 1 : 0.6,
            marginTop: '1rem',
          }}
        >
          {isClearingSessions ? 'Clearing Sessions...' : 'Clear Sessions'}
        </button>
      </div>

      {/* Clear Features */}
      <div
        className="card"
        style={{
          border: '2px solid var(--secondary)',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
        }}
      >
        <h2 style={{ color: 'var(--secondary)', marginBottom: '1rem' }}>
          {Icons.delete} Clear Features &amp; Sub-Dependencies
        </h2>
        <p className="subtitle mb-md">This will permanently delete:</p>
        <ul className="subtitle mb-md" style={{ paddingLeft: '1.5rem' }}>
          <li>All features</li>
          <li>All sub-dependencies</li>
        </ul>
        <p
          style={{
            color: 'var(--secondary)',
            fontWeight: 'bold',
            marginBottom: '1rem',
          }}
        >
          Products and sessions will remain.
        </p>

        <button
          className="btn"
          onClick={handleClearFeatures}
          disabled={isClearingFeatures}
          style={{
            backgroundColor: !isClearingFeatures ? '#9b59b6' : '#95a5a6',
            color: 'white',
            cursor: !isClearingFeatures ? 'pointer' : 'not-allowed',
            opacity: !isClearingFeatures ? 1 : 0.6,
            marginTop: '1rem',
          }}
        >
          {isClearingFeatures ? 'Clearing Features...' : 'Clear Features & Sub-Dependencies'}
        </button>
      </div>

      {/* Wipe Database */}
      <div
        className="card"
        style={{
          border: '2px solid var(--danger)',
          backgroundColor: 'var(--danger-transparent)',
        }}
      >
        <h2 style={{ color: 'var(--danger)', marginBottom: '1rem' }}>
          ⚠️ Wipe Entire Database
        </h2>
        <p className="subtitle mb-md">This will permanently delete ALL data including:</p>
        <ul className="subtitle mb-md" style={{ paddingLeft: '1.5rem' }}>
          <li>All products</li>
          <li>All features</li>
          <li>All sub-dependencies</li>
          <li>All testing sessions</li>
          <li>All audit logs</li>
          <li>All API links</li>
        </ul>
        <p
          style={{
            color: 'var(--danger)',
            fontWeight: 'bold',
            marginBottom: '1rem',
          }}
        >
          This action cannot be undone!
        </p>

        <div className="form-group">
          <label htmlFor="confirm-input">
            Type <strong>CONFIRM</strong> to proceed:
          </label>
          <input
            id="confirm-input"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type CONFIRM here"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e74c3c',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
            disabled={isWiping}
          />
        </div>

        <button
          className="btn"
          onClick={handleWipeDatabase}
          disabled={confirmText !== 'CONFIRM' || isWiping}
          style={{
            backgroundColor:
              confirmText === 'CONFIRM' && !isWiping ? '#e74c3c' : '#95a5a6',
            color: 'white',
            cursor:
              confirmText === 'CONFIRM' && !isWiping ? 'pointer' : 'not-allowed',
            opacity: confirmText === 'CONFIRM' && !isWiping ? 1 : 0.6,
            marginTop: '1rem',
          }}
        >
          {isWiping ? 'Wiping Database...' : 'Wipe Entire Database'}
        </button>
      </div>
    </div>
  );
};

export default AdminToolsSection;
